import { FC, useEffect, useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import Typography from "@/components/Typography";
import { useSearchJobsQuery, JobType, JOB_STATUS } from "@/fetch/suppliers";
import {
  useUpdateInterestedJobsBulkMutation,
  useDeleteInterestedJobMutation,
} from "@/fetch/trip";
import { useTrackers, useSnackbar, useResponsive } from "@/hooks";
import { Skeleton } from "@/components/Loader";
import { EmptyJobsList } from "@/components/EmptyList";
import Alert from "@/components/Alert";
import { InfoIcon } from "@/components/Icon";
import MobilePopup from "@/components/MobilePopup";
import InterestedJobCard from "./InterestedJobCard";
import styles from "./MyInterestsTab.module.scss";

const PAGE_SIZE = 30;

//  Reordering the result helper function
const reorder = <SortableItem extends { id: string } & object>(
  list: Array<JobType>,
  startIndex: number,
  endIndex: number
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

type Props = {};

const MyInterestsTab: FC<Props> = () => {
  const [isInfoPopupOpen, setIsInfoPopupOpen] = useState<boolean>(false);

  const {
    data: jobsData,
    isLoading,
    refetch,
  } = useSearchJobsQuery(
    {
      page: 1,
      limit: PAGE_SIZE,
      isInterested: true,
    },
    ""
  );

  const { mutateAsync: removeInterest, isLoading: isRemovingInterest } =
    useDeleteInterestedJobMutation();

  const { mutateAsync: updateJobs, isLoading: isUpdatingJobs } =
    useUpdateInterestedJobsBulkMutation();

  const { track } = useTrackers();
  const { enqueueSnackbar } = useSnackbar();
  const { isMobile } = useResponsive();

  useEffect(() => {
    track("My Interests Tab Viewed", {
      eventId: "my-interests-tab-viewed",
    });
  }, []);

  const jobs = [...(jobsData?.data || [])].sort((firstJob, secondJob) => {
    if (
      firstJob.status === JOB_STATUS.Active &&
      secondJob.status !== JOB_STATUS.Active
    )
      return -1;
    if (
      firstJob.status !== JOB_STATUS.Active &&
      secondJob.status === JOB_STATUS.Active
    )
      return 1;
    return 0;
  });

  const sortedInterestedJobs = [...(jobs || [])].sort(
    (firstJob, secondJob) => firstJob.order - secondJob.order
  );

  const handleUpdateJobs = async (newSortedJobs: Array<JobType>) => {
    try {
      if (!newSortedJobs.length) {
        refetch();
        return;
      }
      await updateJobs(
        newSortedJobs.map((each, idx) => ({
          id: each.interested_id || 0,
          job_id: each.id,
          order: idx + 1,
        }))
      );
      track("Interested List Reordered", {
        eventId: "interested-list-reordered",
      });
      refetch();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Sorry, something went wrong!", {
        variant: "error",
      });
      refetch();
    }
  };

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      sortedInterestedJobs,
      result.source.index,
      result.destination.index
    );

    handleUpdateJobs(newItems);
  };

  const handleReorder = (deletedJobId: number) => {
    const remainingJobs = sortedInterestedJobs.filter(
      (each) => each.id !== deletedJobId
    );
    handleUpdateJobs(remainingJobs);
  };

  const handleRemoveInterest = async (job: JobType) => {
    try {
      if (!job) return;
      await removeInterest({ interestedJobId: job.interested_id || 0 });
      track("Interested Job Removed", {
        eventId: "interested-job-removed",
        jobId: job.id,
      });
      handleReorder(job.id);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Sorry, something went wrong!", {
        variant: "error",
      });
    }
  };

  return (
    <div className={styles.container}>
      {!isMobile && (
        <Alert variant="standard" color="info" className={styles.banner}>
          You may only express interest in up to 5 jobs. Please prioritise the
          opportunities that best align with your qualifications and interests.
          <br />
          Note: Only jobs that are currently active are counted towards this
          limit.
        </Alert>
      )}
      {isLoading ? (
        <Skeleton
          height={100}
          variant="rectangular"
          className={styles.loader}
        />
      ) : (
        <>
          {sortedInterestedJobs.length > 0 ? (
            <>
              <Typography variant="h6" className={styles.interestTitle}>
                My Interests
                {isMobile && (
                  <InfoIcon
                    variant="outlined"
                    className={styles.infoIcon}
                    onClick={() => setIsInfoPopupOpen(true)}
                  />
                )}
              </Typography>
              {/* <Typography
                variant="body1"
                color="text.secondary"
                className={styles.interestDescription}
              >
                List your job preferences in order
              </Typography> */}
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided, snapshot) => (
                    <div
                      id="interested-job-list-reorder"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {sortedInterestedJobs?.map((eachJob, index) => (
                        <Draggable
                          isDragDisabled={false}
                          key={eachJob.id}
                          draggableId={eachJob?.id.toString() || "id"}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <InterestedJobCard
                                key={eachJob.id}
                                job={eachJob}
                                provided={provided}
                                onDelete={handleRemoveInterest}
                                isLoading={isUpdatingJobs || isRemovingInterest}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          ) : (
            <EmptyJobsList variant="interested" />
          )}
        </>
      )}
      {isInfoPopupOpen && isMobile && (
        <MobilePopup onClose={() => setIsInfoPopupOpen(false)}>
          <div className={styles.infoDialogContainer}>
            <Typography variant="body1" className={styles.mobilePopupText}>
              You may only express interest in up to 5 jobs. Please prioritise
              the opportunities that best align with your qualifications and
              interests.
              <br />
              Note: Only jobs that are currently active are counted towards this
              limit.
            </Typography>
          </div>
        </MobilePopup>
      )}
    </div>
  );
};

export default MyInterestsTab;
